import { z } from 'zod';

import { PLATE_STATE_OPTIONS } from '@/constants';
import {
  PLATE_CHARS_MESSAGE,
  zodDuration,
  zodTimeStamp,
} from '@/utils/zodUtils';

const ChalkStatusEnum = z.enum([
  'onsite',
  'departed',
  'closed',
  'expired',
  'passed',
]);

const ChalkVehicleSchema = z.object({
  make: z.string().optional(),
  model: z.string().optional(),
  color: z.string().optional(),
});

const ChalkReasonResponseSchema = z.object({
  label: z.string(),
  duration: zodDuration,
});

export const ChalkSchema = z.object({
  chalkId: z.string(),
  locationId: z.string(),
  status: ChalkStatusEnum,
  vehicle: ChalkVehicleSchema,
  reason: ChalkReasonResponseSchema,
  createdAt: zodTimeStamp,
  expiresAt: zodTimeStamp,
  blockFaceID: z.string().uuid().nullish(),
  sessionId: z.string().nullish(),
  imagePaths: z.array(z.string()).nullish(),
  presignedImageURLs: z.array(z.string()).nullish(),
});

export type ChalkType = z.infer<typeof ChalkSchema>;

export const CreateChalkSchema = z.object({
  locationId: z.string().min(1, 'Location is required'),
  plate: z
    .string()
    .min(1, 'Plate is required')
    .max(10, 'Plate cannot exceed 10 characters')
    .regex(/^[A-Za-z0-9]+$/, PLATE_CHARS_MESSAGE),
  plateState: z.enum(
    PLATE_STATE_OPTIONS.map((s) => s.abbreviation) as [string, ...string[]],
    {
      errorMap: () => ({ message: 'Invalid US state abbreviation' }),
    }
  ),
  vehicleMake: z.string().min(1, 'Vehicle make is required'),
  vehicleModel: z.string().min(1, 'Vehicle model is required'),
  vehicleColor: z.string().min(1, 'Vehicle color is required'),
  reason: z.string().min(1, 'Chalk reason is required'),
  blockFaceId: z.string().uuid().optional(),
  images: z
    .array(z.instanceof(File))
    .max(2, 'Maximum 2 images allowed')
    .refine(
      (files) => files.every((f) => f.size <= 5 * 1024 * 1024),
      'Each image must be less than 5MB'
    )
    .refine(
      (files) =>
        files.every((f) => ['image/png', 'image/jpeg'].includes(f.type)),
      'Only PNG and JPEG images are allowed'
    )
    .optional(),
});

export type CreateChalkInput = z.infer<typeof CreateChalkSchema>;
